<template>
  <div class="dashboard">
    <!-- 📆 Section principale (Calendrier) -->
    <div class="main-content">
      <div class="month-nav">
        <button @click="prevMonth">◄</button>
        <h2>{{ currentMonth }}</h2>
        <button @click="nextMonth">►</button>
      </div>

      <div class="calendar">
        <div
          v-for="(day, index) in daysInMonth"
          :key="index"
          class="day"
          @click="goToTradeList(day)"
        >
          <div>{{ day }}</div>

          <!-- Indicateurs de trades pour chaque jour -->
          <div v-if="tradeResults[day]" class="trade-indicators">
            <div
              v-for="(status, tradeIndex) in tradeResults[day]"
              :key="tradeIndex"
              class="trade-indicator"
              :class="status"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Barre latérale droite -->
    <div class="sidebar">
      <div class="stats">
        WR :
        <p class="wr">{{ winRate }}%</p>
      </div>

      <!-- SECTION RULES MODIFIABLE -->
      <div class="rules">
        <div class="rules-title">
          <button @click="toggleEditRules">
            {{ editingRules ? "💾" : "🖊️" }}
          </button>
          RULES :
        </div>
        <ul v-if="!editingRules">
          <li v-for="(rule, index) in rules" :key="index">{{ rule }}</li>
        </ul>
        <div v-else>
          <div
            v-for="(rule, index) in rules"
            :key="index"
            class="rule-input-container"
          >
            <input v-model="rules[index]" class="rule-input" />
            <button @click="removeRule(index)" class="remove-btn">❌</button>
          </div>
          <button @click="addRule" class="add-btn">➕</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  doc, // ✅ Importation ajoutée
  getDoc, // ✅ Importation ajoutée
  setDoc, // ✅ Importation ajoutée
} from "firebase/firestore";

export default defineComponent({
  name: "DashboardPage",
  setup() {
    const router = useRouter();
    const auth = getAuth();
    const db = getFirestore();

    const userId = auth.currentUser?.uid;
    const currentMonthIndex = ref(new Date().getMonth());
    const winRate = ref(0);

    const rules = ref<string[]>([
      "📌 TRADE SESSION",
      "🚫 DON'T OVER TRADE",
      "✅ A+ SETUP ONLY",
    ]);
    const editingRules = ref(false);

    // 🔥 Récupération des règles depuis Firebase
    const fetchRules = async () => {
      if (!userId) return;

      const rulesDocRef = doc(db, `users/${userId}`);
      const rulesSnapshot = await getDoc(rulesDocRef);

      if (rulesSnapshot.exists() && rulesSnapshot.data().rules) {
        rules.value = rulesSnapshot.data().rules;
      }
    };

    // 🔥 Sauvegarde des règles dans Firebase
    const saveRules = async () => {
      if (!userId) return;

      const userDocRef = doc(db, `users/${userId}`);
      await setDoc(userDocRef, { rules: rules.value }, { merge: true });
    };

    // 🔄 Toggle mode édition et sauvegarde des règles
    const toggleEditRules = async () => {
      if (editingRules.value) {
        await saveRules();
      }
      editingRules.value = !editingRules.value;
    };

    // ➕ Ajouter une nouvelle règle vide
    const addRule = () => {
      rules.value.push("");
    };

    // ❌ Supprimer une règle
    const removeRule = (index: number) => {
      rules.value.splice(index, 1);
    };

    const tradeResults = ref<Record<number, string[]>>({});

    const fetchMonthlyTradeResults = async () => {
      if (!userId) {
        console.error("Utilisateur non authentifié");
        return;
      }

      const tradesCollection = collection(db, `users/${userId}/trades`);
      const querySnapshot = await getDocs(tradesCollection);

      const tempTradeResults: Record<number, string[]> = {};
      const year = new Date().getFullYear();
      const month = String(currentMonthIndex.value + 1).padStart(2, "0");

      querySnapshot.forEach((doc) => {
        const tradeData = doc.data();
        if (!tradeData.date) {
          console.warn(
            `❌ Pas de champ 'date' trouvé pour le document : ${doc.id}`
          );
          return;
        }

        const tradeDate = tradeData.date;
        const dateParts = tradeDate.split("-");
        if (dateParts.length !== 3) {
          console.warn(`❌ Format de date invalide : ${tradeDate}`);
          return;
        }

        const tradeYear = parseInt(dateParts[0], 10);
        const tradeMonth = parseInt(dateParts[1], 10) - 1;
        const tradeDay = parseInt(dateParts[2], 10);

        if (tradeYear === year && tradeMonth === currentMonthIndex.value) {
          if (!tempTradeResults[tradeDay]) {
            tempTradeResults[tradeDay] = [];
          }
          tempTradeResults[tradeDay].push(
            tradeData.status === "Win" ? "win" : "loss"
          );
        }
      });

      tradeResults.value = tempTradeResults;
    };

    const monthNames = [
      "JANUARY",
      "FEBRUARY",
      "MARCH",
      "APRIL",
      "MAY",
      "JUNE",
      "JULY",
      "AUGUST",
      "SEPTEMBER",
      "OCTOBER",
      "NOVEMBER",
      "DECEMBER",
    ];
    const currentMonth = computed(() => monthNames[currentMonthIndex.value]);

    const daysInMonth = computed(() => {
      const year = new Date().getFullYear();
      return Array.from(
        { length: new Date(year, currentMonthIndex.value + 1, 0).getDate() },
        (_, i) => i + 1
      );
    });

    const goToTradeList = (day: number) => {
      const year = new Date().getFullYear();
      const month = String(currentMonthIndex.value + 1).padStart(2, "0");
      const dayFormatted = String(day).padStart(2, "0");
      const formattedDate = `${year}-${month}-${dayFormatted}`;

      router.push(`/trades/${formattedDate}`);
    };

    // Récupération de tous les trades de l'utilisateur et calcul du win rate global
    const fetchGlobalWinRate = async () => {
      if (!userId) {
        console.error("Utilisateur non authentifié");
        return;
      }

      const tradesCollection = collection(db, `users/${userId}/trades`);
      const querySnapshot = await getDocs(tradesCollection);

      let winCount = 0;
      let totalTrades = 0;

      querySnapshot.forEach((doc) => {
        const tradeData = doc.data();
        totalTrades++;
        if (tradeData.status === "Win") {
          winCount++;
        }
      });

      winRate.value =
        totalTrades > 0 ? Math.round((winCount / totalTrades) * 100) : 0;
    };

    const prevMonth = () => {
      currentMonthIndex.value = (currentMonthIndex.value - 1 + 12) % 12;
    };

    const nextMonth = () => {
      currentMonthIndex.value = (currentMonthIndex.value + 1) % 12;
    };

    // ⏳ Initialisation
    onMounted(async () => {
      await fetchRules();
      await fetchMonthlyTradeResults();
      await fetchGlobalWinRate();
    });

    watch(currentMonthIndex, fetchMonthlyTradeResults, { immediate: true });

    return {
      currentMonth,
      daysInMonth,
      winRate,
      prevMonth,
      nextMonth,
      goToTradeList,
      tradeResults, // ✅ Ajout pour être accessible au template
      rules,
      editingRules,
      toggleEditRules,
      addRule,
      removeRule,
    };
  },
});
</script>

<style scoped>
/* Conteneur principal */
.dashboard {
  display: flex;
  height: 95vh; /* Prend toute la hauteur de la fenêtre */
  background: #191821;
  padding: 20px;
  border-radius: 20px;
  color: white;
  gap: 20px;
}

/* Section principale (Calendrier) */
.main-content {
  flex: 1; /* Prend tout l'espace disponible */
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Barre de navigation du mois */
.month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #282736;
  width: 99%;
  padding: 15px;
  border-radius: 10px;
  font-size: 20px;
  font-weight: bold;
  margin: 10px;
}

.month-nav button {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
}

/* Section du calendrier */
.calendar {
  display: grid;
  grid-template-columns: repeat(7, 1fr); /* 7 colonnes pour le mois */
  gap: 20px;
  background: #191821;
  padding: 15px;
  border-radius: 10px;
  width: 100%;
  height: 100%;
}

.day {
  background: #484848;
  color: white;
  padding: 25px;
  text-align: center;
  border-radius: 40px;
  cursor: pointer;
  transition: 0.3s;
  font-size: 20px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day:hover {
  background: #ffcc00;
  color: black;
}

/* Barre latérale */
.sidebar {
  margin: 10px;
  width: 250px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  background-color: #191821;
}

/* Bloc du Win Rate */
.stats {
  background: #282736;
  padding: 30px;
  text-align: left;
  border-radius: 20px;
  font-size: 50px;
  font-weight: bold;
  height: 50%;
}

.wr {
  font-size: 80px;
  font-weight: bold;
  color: white;
  text-align: center;
}

/* Bloc des Règles */
.rules {
  background: #4a3228;
  padding: 20px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: bold;
  position: relative;
  overflow: hidden;
  height: 50%;
}

.rules-title {
  justify-content: space-between;
  text-align: left;
  font-size: 50px;
}

.rules::after {
  content: "";
  /*background: url("/path-to-your-image.png") no-repeat center;*/
  background-size: cover;
  position: absolute;
  bottom: 0;
  right: 0;
  width: 50%;
  height: 100%;
  opacity: 0.5;
}

.rules h3 {
  text-transform: uppercase;
}

.rules ul {
  list-style-type: none;
  padding: 0;
}

.rules li {
  padding: 5px 0;
}

/* 📱 Responsive */
@media (max-width: 1024px) {
  .dashboard {
    flex-direction: column;
    align-items: center;
  }

  .sidebar {
    width: 100%;
    flex-direction: row;
    justify-content: space-around;
  }

  .calendar {
    grid-template-columns: repeat(7, 1fr);
  }

  .rules::after {
    width: 100%;
  }
}

/* Conteneur des indicateurs */
.trade-indicators {
  display: flex;
  justify-content: center;
  gap: 2px;
  flex-wrap: wrap;
  margin-top: 5px;
}

/* Petit carré pour chaque trade */
.trade-indicator {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

/* Couleurs des indicateurs */
.win {
  background-color: green;
}

.loss {
  background-color: red;
}
</style>
